import {
    CookiePreference,
    CookiePreferences,
    CookiePreferencesResult,
    OpenTicketCookiesInjector as CookiesInjector,
    OpenTicketCookiesInjectorConfig,
} from '@openticket/lib-cookies';
import { CookiesModalConfig } from '@openticket/lib-cookies/lib/cookies_modal';
import { OpenTicketShop } from '@openticket/sdk-shop';
import {
    ClientWithCookiePreferences,
    ClientWithStrictMode,
    CookieMessagingResult,
    PopupOrEmbedClient,
} from './types';

export async function registerCookiePreferences(
    shop: OpenTicketShop,
    cookieModalTexts: CookiesModalConfig['texts'],
    cb: (preferences?: CookiePreferences) => void
): Promise<void> {
    const cookies = new CookiesInjector();

    const cookiePreferences = await waitForMessagingPreferencesOrInitCookies(
        shop,
        cookies,
        cookieModalTexts
    );

    cookies.on('accept', (_, newPreferences?: CookiePreferences) => {
        cb(newPreferences);
    });

    cookies.on('update', (_, newPreferences?: CookiePreferences) => {
        cb(newPreferences);
    });

    cookies.on('clear', (_, newPreferences?: CookiePreferences) => {
        cb(newPreferences);
    });

    // Apply preferences or request if not available
    if (cookiePreferences) {
        cb(cookiePreferences);
    } else {
        void cookies.openCookiesModal();
    }

    // TODO cookies will need to be initialised always.
    // It should be possible to open the modal later to change the behavior.
}

export async function waitForMessagingPreferencesOrInitCookies(
    shop: OpenTicketShop,
    cookies: CookiesInjector,
    cookieModalTexts: CookiesModalConfig['texts']
): Promise<CookiePreferencesResult> {
    const messagingResult: CookieMessagingResult = await waitForMessagingPreferences(
        shop
    );

    console.log('injectedCookieValues', messagingResult);

    if (messagingResult.cookiePreferences) {
        return messagingResult.cookiePreferences;
    }

    // TODO: Prepared only lib-cookies does not have it implemented
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const strict: boolean = getStrictValue(messagingResult.strict, false);

    const config: Partial<OpenTicketCookiesInjectorConfig> = {
        modal: {
            position: 'top',
            // strict,
            texts: cookieModalTexts,
        },
    };

    return cookies.init(config);
}

function hasProperty<K extends PropertyKey>(
    value: unknown,
    key: K
): value is Record<K, unknown> {
    return (
        !!value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        key in value
    );
}

function clientIsPopupOrEmbed(client: unknown): client is PopupOrEmbedClient {
    return (
        hasProperty(client, 'type') &&
        (client.type === 'popup' || client.type === 'embed')
    );
}

function clientHasCookiePreferences(
    client: PopupOrEmbedClient
): client is ClientWithCookiePreferences {
    return (
        hasProperty(client, 'cookiePreferences') &&
        !!client.cookiePreferences &&
        typeof client.cookiePreferences === 'object' &&
        !Array.isArray(client.cookiePreferences)
    );
}

function clientHasStrictMode(
    client: PopupOrEmbedClient
): client is ClientWithStrictMode {
    return hasProperty(client, 'strict') && typeof client.strict === 'boolean';
}

async function waitForMessagingPreferences(
    shop: OpenTicketShop
): Promise<CookieMessagingResult> {
    const result: CookieMessagingResult = {};

    if (window.self !== window.top && shop.messaging) {
        try {
            await withTimeout(shop.messaging.connecting, 3000);
        } catch (_e) {
            // Ignore, could not find messaging client's preferences
        }

        if (clientIsPopupOrEmbed(shop.messaging.client)) {
            if (clientHasCookiePreferences(shop.messaging.client)) {
                const {
                    saveOnDevice,
                    baseAds,
                    personalisedAds,
                } = shop.messaging.client.cookiePreferences;

                result.cookiePreferences = {
                    [CookiePreference.SaveOnDevice]: !!saveOnDevice,
                    [CookiePreference.BaseAds]: !!baseAds,
                    [CookiePreference.PersonalisedAds]: !!personalisedAds,
                };
            }

            if (clientHasStrictMode(shop.messaging.client)) {
                result.strict = shop.messaging.client.strict;
            }
        }
    }

    return result;
}

export function withTimeout<T>(
    promise: PromiseLike<T>,
    ms: number
): Promise<Awaited<T>> {
    return Promise.race<T>([
        promise,
        new Promise((_resolve, reject) => window.setTimeout(reject, ms)),
    ]);
}

function getStrictValue(
    messagingStrict: boolean | undefined,
    defaultValue: boolean
): boolean {
    if (new URLSearchParams(window.location.search).has('strict')) {
        return true;
    }

    if (typeof messagingStrict === 'boolean') {
        return messagingStrict;
    }

    return defaultValue;
}
